import React, { useState } from 'react';
import { useBusinessPlan } from '../../App';
import { FileText, CheckSquare, Square, ChevronDown, ChevronRight } from 'lucide-react';
import ComponentExporter from './ComponentExporter';

const PlanReportsExplorer = () => {
  const { currentPlan } = useBusinessPlan();
  
  const [selectedSections, setSelectedSections] = useState([]);
  const [expandedSections, setExpandedSections] = useState([]);
  
  // Available sections with sub-sections for export
  const availableSections = [
    {
      id: 'strategic_framework',
      name: 'Strategic Framework',
      category: 'Input Sections',
      icon: '📋',
      subSections: [
        { id: 'objectives', name: 'Objectives', description: 'Goals with targets, status, owners' },
        { id: 'initiatives', name: 'Initiatives', description: 'Budgets, timelines, linked objectives' },
        { id: 'revenue_streams', name: 'Revenue Streams', description: 'Types, projections, models' },
        { id: 'products', name: 'Products & Services', description: 'Pricing, units, SKUs, details' },
        { id: 'sales_analytics', name: 'Sales Analytics', description: 'Per product revenue breakdown, growth, trends' }
      ]
    },
    {
      id: 'cost_centers',
      name: 'Cost Center Management',
      category: 'Input Sections',
      icon: '💰',
      subSections: [
        { id: 'overview', name: 'Cost Center Overview', description: 'Structure, hierarchy' },
        { id: 'allocations', name: 'Budget Allocations', description: 'By center, by department' },
        { id: 'cost_breakdown', name: 'Direct vs Indirect Costs', description: 'Detailed breakdown' }
      ]
    },
    {
      id: 'cost_management',
      name: 'Cost Management',
      category: 'Input Sections',
      icon: '💸',
      subSections: [
        { id: 'cogs', name: 'COGS', description: 'Cost of Goods Sold by product' },
        { id: 'opex', name: 'Operating Expenses', description: 'By category' },
        { id: 'fixed_variable', name: 'Fixed vs Variable Costs', description: 'Cost structure' },
        { id: 'cost_per_unit', name: 'Cost per Unit Analysis', description: 'Unit economics' },
        { id: 'margin_analysis', name: 'Margin Analysis', description: 'Gross and net margins' }
      ]
    },
    {
      id: 'financial_management',
      name: 'Financial Management',
      category: 'Input Sections',
      icon: '💵',
      subSections: [
        { id: 'working_capital', name: 'Working Capital', description: 'Current assets, liabilities' },
        { id: 'assets', name: 'Assets', description: 'Fixed, current, intangible' },
        { id: 'liabilities', name: 'Liabilities', description: 'Short-term, long-term' },
        { id: 'equity', name: 'Equity Structure', description: 'Ownership, retained earnings' }
      ]
    },
    {
      id: 'financial_planning',
      name: 'Financial Planning',
      category: 'Input Sections',
      icon: '📊',
      subSections: [
        { id: 'revenue_projections', name: 'Revenue Projections', description: 'By product, by year' },
        { id: 'unit_projections', name: 'Unit Sales Projections', description: 'Volume forecasts' },
        { id: 'pricing', name: 'Pricing Strategy', description: 'Price points, changes' },
        { id: 'growth', name: 'Growth Assumptions', description: 'Growth rates, drivers' },
        { id: 'market_penetration', name: 'Market Penetration', description: 'Market share targets' }
      ]
    },
    {
      id: 'financial_statements',
      name: 'Financial Statements',
      category: 'Output Sections',
      icon: '📈',
      subSections: [
        { id: 'income_statement', name: 'Income Statement', description: '3-year P&L projection' },
        { id: 'balance_sheet', name: 'Balance Sheet', description: 'Projected balance sheet' },
        { id: 'cash_flow', name: 'Cash Flow Statement', description: 'Projected cash flows' },
        { id: 'ratios', name: 'Key Financial Ratios', description: 'Performance ratios' }
      ]
    },
    {
      id: 'financial_analytics',
      name: 'Financial Analytics',
      category: 'Output Sections',
      icon: '📊',
      subSections: [
        { id: 'profitability', name: 'Profitability Ratios', description: 'Margins, ROI, ROE' },
        { id: 'liquidity', name: 'Liquidity Ratios', description: 'Current, quick ratios' },
        { id: 'efficiency', name: 'Efficiency Ratios', description: 'Asset turnover, inventory' },
        { id: 'growth_metrics', name: 'Growth Metrics', description: 'YoY growth, CAGR' }
      ]
    },
    {
      id: 'planning_dashboard',
      name: 'Planning Dashboard',
      category: 'Output Sections',
      icon: '🎯',
      subSections: [
        { id: 'executive_summary', name: 'Executive Summary', description: 'High-level overview' },
        { id: 'kpis', name: 'Key KPIs', description: 'Critical metrics' },
        { id: 'performance', name: 'Performance Indicators', description: 'Tracking metrics' }
      ]
    },
    {
      id: 'scenario_planning',
      name: 'Strategic Scenario Planning',
      category: 'Output Sections',
      icon: '🔮',
      subSections: [
        { id: 'base_case', name: 'Base Case Scenario', description: 'Expected outcome' },
        { id: 'optimistic', name: 'Optimistic Scenario', description: 'Best case' },
        { id: 'pessimistic', name: 'Pessimistic Scenario', description: 'Worst case' },
        { id: 'sensitivity', name: 'Sensitivity Analysis', description: 'Variable impact' }
      ]
    }
  ];

  const toggleSection = (sectionId) => {
    if (expandedSections.includes(sectionId)) {
      setExpandedSections(expandedSections.filter(id => id !== sectionId));
    } else {
      setExpandedSections([...expandedSections, sectionId]);
    }
  };

  const toggleSubSection = (sectionId, subSectionId) => {
    const key = `${sectionId}.${subSectionId}`;
    if (selectedSections.includes(key)) {
      setSelectedSections(selectedSections.filter(id => id !== key));
    } else {
      setSelectedSections([...selectedSections, key]);
    }
  };

  const selectAllInSection = (section) => {
    const sectionKeys = section.subSections.map(sub => `${section.id}.${sub.id}`);
    const allSelected = sectionKeys.every(key => selectedSections.includes(key));
    
    if (allSelected) {
      // Deselect all
      setSelectedSections(selectedSections.filter(key => !sectionKeys.includes(key)));
    } else {
      // Select all
      const newSelections = [...selectedSections];
      sectionKeys.forEach(key => {
        if (!newSelections.includes(key)) {
          newSelections.push(key);
        }
      });
      setSelectedSections(newSelections);
    }
  };

  const groupedSections = availableSections.reduce((acc, section) => {
    if (!acc[section.category]) {
      acc[section.category] = [];
    }
    acc[section.category].push(section);
    return acc;
  }, {});

  const getSelectedCount = (section) => {
    return section.subSections.filter(sub => 
      selectedSections.includes(`${section.id}.${sub.id}`)
    ).length;
  };

  if (!currentPlan) {
    return (
      <div className="p-8">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Business Plan Selected</h3>
          <p className="text-gray-600">
            Please select or create a business plan to export plan documents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Plan Export - Detailed Selection</h1>
          <p className="text-gray-600 mt-1">
            Select specific sub-sections to include in your business plan export
          </p>
          <div className="mt-2 px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded inline-block">
            ✨ Comprehensive export with all tabs, tables, and analytics from each section
          </div>
        </div>
      </div>

      {/* Section Selection */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Select Sections & Sub-Sections</h2>
        <p className="text-sm text-gray-600 mb-6">
          Expand each section to select specific components. Each sub-section includes all relevant data, tables, charts, and analytics.
        </p>

        <div className="space-y-4">
          {Object.entries(groupedSections).map(([category, sections]) => (
            <div key={category}>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">{category}</h3>
              <div className="space-y-2">
                {sections.map((section) => {
                  const isExpanded = expandedSections.includes(section.id);
                  const selectedCount = getSelectedCount(section);
                  const totalCount = section.subSections.length;
                  
                  return (
                    <div key={section.id} className="border border-gray-200 rounded-lg">
                      {/* Main Section Header */}
                      <div className="flex items-center justify-between p-4 bg-gray-50">
                        <div 
                          className="flex items-center flex-1 cursor-pointer"
                          onClick={() => toggleSection(section.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-5 w-5 text-gray-500 mr-2" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-500 mr-2" />
                          )}
                          <span className="text-2xl mr-3">{section.icon}</span>
                          <div>
                            <p className="font-semibold text-gray-900">{section.name}</p>
                            <p className="text-xs text-gray-500">
                              {selectedCount} of {totalCount} sub-sections selected
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            selectAllInSection(section);
                          }}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium px-3 py-1 rounded hover:bg-blue-50"
                        >
                          {selectedCount === totalCount ? 'Deselect All' : 'Select All'}
                        </button>
                      </div>

                      {/* Sub-sections */}
                      {isExpanded && (
                        <div className="p-4 space-y-2 bg-white">
                          {section.subSections.map((subSection) => {
                            const key = `${section.id}.${subSection.id}`;
                            const isSelected = selectedSections.includes(key);
                            
                            return (
                              <div
                                key={subSection.id}
                                onClick={() => toggleSubSection(section.id, subSection.id)}
                                className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${
                                  isSelected
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {isSelected ? (
                                  <CheckSquare className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <Square className="h-5 w-5 text-gray-400 mr-3 mt-0.5 flex-shrink-0" />
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{subSection.name}</p>
                                  <p className="text-xs text-gray-600 mt-1">{subSection.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Selected count */}
        {selectedSections.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-lg text-blue-600">{selectedSections.length}</span> sub-section(s) selected for export
            </p>
            <button
              onClick={() => setSelectedSections([])}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Clear All Selections
            </button>
          </div>
        )}
      </div>

      {/* Export Actions */}
      {selectedSections.length > 0 ? (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Generate Comprehensive Export</h2>
          <p className="text-sm text-gray-600 mb-4">
            This will generate a detailed document with all selected sub-sections including tables, metrics, and analytics.
          </p>
          <ComponentExporter
            selectedSections={selectedSections}
            planName={currentPlan?.name}
            onComplete={() => alert('✅ Comprehensive export complete! Check your downloads folder.')}
          />
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-600 mb-2 font-medium">
            No sub-sections selected
          </p>
          <p className="text-sm text-gray-500">
            Expand sections above and select specific components to include in your export
          </p>
        </div>
      )}
    </div>
  );
};

export default PlanReportsExplorer;
